import type { FlowAnalysisPlay, FlowAnalysisStrategy } from 'constants/payloads/flowAnalysis';
import { getOpcStrategyPage, getTickerFlowPage } from 'constants/pages';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
/** Keeps a time with its AM/PM and zone, and a month with its day, on one line. */
const NBSP = ' ';

/** The same tags the producer's markdown links carry, so attribution does not change. */
const UTM = 'utm_source=flow&utm_medium=modal&utm_campaign=modal_text';

const STRATEGY_SLUG: Record<FlowAnalysisStrategy, string> = {
  'Long Call': 'long-call',
  'Long Put': 'long-put',
  'Bull Call Spread': 'bull-call-spread',
  'Bear Put Spread': 'bear-put-spread',
  'Iron Condor': 'iron-condor',
};

interface EtStamp {
  year: number;
  month: number;
  day: number;
  /** "10:39 AM", with the hour unpadded. */
  time: string;
}

/** "2026-10-9 10:39 AM" (how the producer writes every Eastern time) as its parts. */
export const parseEtStamp = (stamp: string): EtStamp | null => {
  const match = stamp.match(/^(\d{4})-(\d{1,2})-(\d{1,2}) (\d{1,2}):(\d{2}) ([AP]M)$/);
  if (!match) return null;
  const month = Number(match[2]);
  if (month < 1 || month > 12) return null;
  return {
    year: Number(match[1]),
    month,
    day: Number(match[3]),
    time: `${Number(match[4])}:${match[5]} ${match[6]}`,
  };
};

/**
 * "10:39 AM ET" on the run's own day, "Oct 5, 12:25 PM ET" on another, with the year when it
 * differs from the run's. Times inside the report are Eastern, so the stamp says so.
 */
export const formatEtStamp = (stamp: string, runAtEt: string): string => {
  const at = parseEtStamp(stamp);
  if (!at) return stamp;
  const time = `${at.time.replace(' ', NBSP)}${NBSP}ET`;
  const run = parseEtStamp(runAtEt);
  if (run && run.year === at.year && run.month === at.month && run.day === at.day) return time;
  const year = !run || run.year !== at.year ? `, ${at.year}` : '';
  return `${MONTHS[at.month - 1]}${NBSP}${at.day}${year}, ${time}`;
};

/** "2027-3-19" as "Mar 19, 2027"; the year is dropped when it is the run's own year. */
export const formatExpiry = (expiry: string, runAtEt: string): string => {
  const match = expiry.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
  if (!match || Number(match[2]) < 1 || Number(match[2]) > 12) return expiry;
  const run = parseEtStamp(runAtEt);
  const year = !run || run.year !== Number(match[1]) ? `, ${match[1]}` : '';
  return `${MONTHS[Number(match[2]) - 1]}${NBSP}${Number(match[3])}${year}`;
};

/** Source precision, with separators for full dollars and the written precision for K/M/B. */
export const formatPremium = (premium: string): string => {
  const match = premium.replace(/,/g, '').match(/^\$(\d+)(\.\d+)?([KMB])?$/i);
  if (!match) return premium;
  const whole = match[3] ? match[1] : match[1].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return `$${whole}${match[2] ?? ''}${match[3]?.toUpperCase() ?? ''}`;
};

/** A machine-readable Eastern civil date/time; the accompanying title supplies its zone. */
export const etStampDateTime = (stamp: string): string | undefined => {
  const at = parseEtStamp(stamp);
  if (!at) return undefined;
  const match = at.time.match(/^(\d{1,2}):(\d{2}) ([AP]M)$/)!;
  const hour = (Number(match[1]) % 12) + (match[3] === 'PM' ? 12 : 0);
  return `${at.year}-${String(at.month).padStart(2, '0')}-${String(at.day).padStart(
    2,
    '0',
  )}T${String(hour).padStart(2, '0')}:${match[2]}`;
};

/** 16 → "16% OTM", -6 → "6% ITM", 0 → "ATM". */
export const formatMoneyness = (otmPercent: number): string => {
  if (otmPercent === 0) return 'ATM';
  return otmPercent > 0 ? `${otmPercent}%${NBSP}OTM` : `${-otmPercent}%${NBSP}ITM`;
};

/** 0 → "0DTE", 161 → "161 DTE". */
export const formatDaysToExpiry = (days: number): string =>
  days === 0 ? '0DTE' : `${days}${NBSP}DTE`;

/**
 * Size against open interest, at or above 1×: "1.7× OI", or "12× OI" as the dataset rounds it.
 * A contract with no open interest before the print reads "0 prior OI". Below 1× the print could
 * be a slice of an existing position, so the figure stays off the row.
 */
export const formatSizeVsOi = (ratio: number | 'open'): string | null => {
  if (ratio === 'open') return `0${NBSP}prior${NBSP}OI`;
  if (ratio < 1) return null;
  return `${Number.isInteger(ratio) ? ratio : ratio.toFixed(1)}×${NBSP}OI`;
};

/**
 * Plain hrefs, followed with a full page load as the markdown's absolute links were, so the
 * landing page view still carries the UTM tags into PostHog.
 */
export const tickerFlowHref = (ticker: string) => `${getTickerFlowPage(ticker)}?${UTM}`;

/** The calculator page for the play, with its strategy and ticker already loaded. */
export const playHref = (play: Pick<FlowAnalysisPlay, 'strategy' | 'ticker'>) =>
  `${getOpcStrategyPage(STRATEGY_SLUG[play.strategy], play.ticker)}?${UTM}`;
